import { m, useReducedMotion } from 'motion/react';
import type { Transition } from 'motion/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { useId, useMemo } from 'react';

const SPARKLE_PATH =
  'M 12.41,6.86 C 14.748,7.74 17.082,8.62 19.42,9.5 C 17.082,10.38 14.748,11.26 12.41,12.14 C 11.511,14.427 10.609,16.713 9.71,19 C 8.811,16.713 7.909,14.427 7.01,12.14 C 4.673,11.26 2.337,10.38 0,9.5 C 2.337,8.62 4.673,7.74 7.01,6.86 C 7.909,4.573 8.811,2.287 9.71,0 C 10.609,2.287 11.511,4.573 12.41,6.86 Z';

const SVG_STYLE = { originX: '50%', originY: '50%', flexShrink: 0, opacity: 0.75 } as const;

const ANIMATE = {
  rotate: [0, 90, 180, 270, 360],
  scale: [1, 0.737, 1, 0.737, 1],
};

const TRANSITION: Transition = {
  duration: 5.04,
  times: [0, 0.243, 0.463, 0.763, 1],
  ease: [
    [0.5, 0, 0, 1],
    [0.5, 0, 0, 1],
    [0.5, 0, 0, 1],
    [0.5, 0, 0, 1],
  ],
  repeat: Infinity,
};

type ThinkingIconProps = {
  /** Rendered width and height; defaults to the surrounding font size. */
  size?: number | string;
};

/**
 * The AI sparkle at work: the copilot's thinking indicator, also the standard
 * loading mark for anything the agent is generating (brand documents, styles).
 */
export function ThinkingIcon({ size = '1em' }: ThinkingIconProps) {
  const shouldReduceMotion = useReducedMotion();
  const reactId = useId();
  const gradientId = useMemo(() => `thinking-icon-gradient-${reactId.replace(/:/g, '')}`, [reactId]);

  return (
    <m.svg
      viewBox="0 0 19.42 19"
      width={size}
      height={size}
      aria-hidden="true"
      data-test-id="thinking-icon"
      style={SVG_STYLE}
      animate={shouldReduceMotion === true ? undefined : ANIMATE}
      transition={shouldReduceMotion === true ? undefined : TRANSITION}
    >
      <defs>
        <linearGradient id={gradientId} x1="100%" y1="0%" x2="0%" y2="0%">
          <stop offset="0%" style={{ stopColor: themeVars.palette['orange-500'] }} />
          <stop offset="100%" style={{ stopColor: themeVars.palette['violet-600'] }} />
        </linearGradient>
      </defs>
      <path d={SPARKLE_PATH} fill={`url(#${gradientId})`} />
    </m.svg>
  );
}
